import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDays,
  addMinutes,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { Loader2, CalendarDays, RefreshCcw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { fetchAppointments } from "../../lib/therapistApi";

const formatDateLabel = (date) => format(date, "EEEE, MMM d");
const toISODate = (date) => format(date, "yyyy-MM-dd");

const toDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatTime = (value) => {
  const dateValue = toDateValue(value);
  if (!dateValue) return "";
  return format(dateValue, "h:mm a");
};

const formatLabel = (value) => {
  if (!value) return "";
  const normalized = String(value).replace(/_/g, " ").trim();
  if (!normalized) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

function useTherapistId() {
  const { therapistId } = useAuth();
  return therapistId;
}

const CalendarView = () => {
  const therapistId = useTherapistId();
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get date range for the month
  const monthStart = useMemo(() => startOfMonth(currentMonth), [currentMonth]);
  const monthEnd = useMemo(() => endOfMonth(currentMonth), [currentMonth]);

  const loadAppointments = useCallback(async () => {
    if (!therapistId) {
      console.warn("No therapist ID available, cannot load appointments");
      return;
    }
    try {
      setLoading(true);
      // Fetch entire month of appointments
      const data = await fetchAppointments(therapistId, {
        from: toISODate(monthStart),
        to: toISODate(monthEnd),
      });
      console.log("CalendarView appointments:", data);
      setAppointments(data.appointments || []);
    } catch (error) {
      toast.error(error?.message || "Failed to load appointments");
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [therapistId, monthStart, monthEnd]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const statusSummary = useMemo(() => {
    const summary = { confirmed: 0, pending: 0, completed: 0, cancelled: 0 };
    appointments.forEach((appt) => {
      const key = String(appt.status || "").toLowerCase();
      if (summary[key] !== undefined) summary[key] += 1;
    });
    return summary;
  }, [appointments]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const start = startOfWeek(monthStart);
    const end = endOfWeek(monthEnd);
    const days = [];
    let currentDay = start;

    while (currentDay <= end) {
      days.push(currentDay);
      currentDay = addDays(currentDay, 1);
    }

    return days;
  }, [monthStart, monthEnd]);

  // Group appointments by day
  const appointmentsByDay = useMemo(() => {
    const map = new Map();
    appointments.forEach((appt) => {
      const date =
        toDateValue(appt.appointmentDate) ||
        toDateValue(appt.startDate) ||
        toDateValue(appt.start_at);
      if (date) {
        const dayKey = toISODate(date);
        if (!map.has(dayKey)) {
          map.set(dayKey, []);
        }
        map.get(dayKey).push(appt);
      }
    });
    return map;
  }, [appointments]);

  const navigateMonths = (delta) => {
    setCurrentMonth((prev) => addMonths(prev, delta));
    setSelectedDate(null);
  };

  const resetToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(null);
  };

  // Get selected day's appointments
  const selectedDayAppointments = useMemo(() => {
    if (!selectedDate) return [];
    const dayKey = toISODate(selectedDate);
    return appointmentsByDay.get(dayKey) || [];
  }, [selectedDate, appointmentsByDay]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Monthly Calendar</h1>
          <p className="text-slate-600 mt-1">
            View all your appointments across the month at a glance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetToday}
            disabled={loading}
            className="px-3 py-2">
            Today
          </Button>
          <Button
            variant="default"
            onClick={loadAppointments}
            disabled={loading}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Summary */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          Confirmed: {statusSummary.confirmed}
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
          Pending: {statusSummary.pending}
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          Completed: {statusSummary.completed}
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          Cancelled: {statusSummary.cancelled}
        </div>
      </div>

      {/* Monthly Calendar Grid */}
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold text-slate-900">
              {format(currentMonth, "MMMM yyyy")}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonths(-1)}
                disabled={loading}
                className="px-3">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonths(1)}
                disabled={loading}
                className="px-3">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex h-96 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-2">
              {/* Day headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="text-center font-semibold text-slate-600 text-sm py-2">
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {calendarDays.map((day) => {
                const dayKey = toISODate(day);
                const dayAppointments = appointmentsByDay.get(dayKey) || [];
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, new Date());
                const isSelected = selectedDate && isSameDay(day, selectedDate);

                return (
                  <button
                    key={dayKey}
                    onClick={() => setSelectedDate(day)}
                    className={`
                      min-h-24 p-2 rounded-lg border transition-all
                      ${isCurrentMonth ? "bg-white" : "bg-slate-50"}
                      ${isSelected ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200"}
                      ${isToday && !isSelected ? "border-indigo-300 bg-indigo-50" : ""}
                      ${!isCurrentMonth ? "text-slate-400" : "text-slate-900"}
                      hover:border-indigo-400 hover:shadow-sm
                    `}>
                    <div className="flex flex-col h-full">
                      <span className={`text-sm font-medium ${isToday ? "text-indigo-600 font-bold" : ""}`}>
                        {format(day, "d")}
                      </span>
                      {dayAppointments.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {dayAppointments.slice(0, 2).map((appt, idx) => {
                            const startDate =
                              toDateValue(appt.appointmentDate) ||
                              toDateValue(appt.startDate) ||
                              toDateValue(appt.start_at);
                            const status = String(appt.status || "").toLowerCase();
                            const statusColor =
                              status === "confirmed"
                                ? "bg-emerald-100 text-emerald-700"
                                : status === "pending"
                                ? "bg-amber-100 text-amber-700"
                                : status === "completed"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-red-100 text-red-700";

                            return (
                              <div
                                key={idx}
                                className={`text-xs px-1.5 py-0.5 rounded truncate ${statusColor}`}>
                                {formatTime(startDate)}
                              </div>
                            );
                          })}
                          {dayAppointments.length > 2 && (
                            <div className="text-xs text-slate-500 font-medium">
                              +{dayAppointments.length - 2} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Day Details */}
      {selectedDate && selectedDayAppointments.length > 0 && (
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-white border-b border-slate-200">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-slate-900">
                {formatDateLabel(selectedDate)}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDate(null)}>
                Close
              </Button>
            </div>
            <p className="text-slate-600 mt-1">
              {selectedDayAppointments.length} appointment
              {selectedDayAppointments.length !== 1 ? "s" : ""}
            </p>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6">
            <div className="space-y-4">
              {selectedDayAppointments.map((appt) => {
                const startDate =
                  toDateValue(appt.appointmentDate) ||
                  toDateValue(appt.startDate) ||
                  toDateValue(appt.start_at);
                const durationMinutes =
                  appt.duration ??
                  appt.durationMinutes ??
                  appt.duration_minutes ??
                  appt.durationInMinutes ??
                  null;
                const endDate =
                  toDateValue(appt.endDate) ||
                  toDateValue(appt.end_at) ||
                  (startDate && durationMinutes
                    ? addMinutes(startDate, Number(durationMinutes))
                    : null);
                const timeRange = (() => {
                  const startLabel = formatTime(startDate);
                  const endLabel = formatTime(endDate);
                  if (startLabel && endLabel)
                    return `${startLabel} - ${endLabel}`;
                  if (startLabel) return startLabel;
                  return "Time TBD";
                })();
                const clientLabel =
                  appt.clientName ||
                  appt.client_name ||
                  [appt.clientFirstName, appt.clientLastName]
                    .filter(Boolean)
                    .join(" ") ||
                  appt.clientFullName ||
                  appt.client_email ||
                  appt.clientEmail ||
                  "Client";
                const massageRaw =
                  appt.massageType ||
                  appt.massage_type ||
                  appt.serviceType ||
                  appt.service_type ||
                  appt.serviceName ||
                  appt.service_name ||
                  appt.service ||
                  appt.treatmentType ||
                  appt.treatment_type ||
                  "";
                const massageDisplay = massageRaw
                  ? formatLabel(massageRaw)
                  : "Massage session";
                const durationDisplay = durationMinutes
                  ? `${durationMinutes} min`
                  : "-";
                const statusLabel = formatLabel(appt.status);
                const notes = appt.notes;

                return (
                  <div
                    key={String(appt.id || `${clientLabel}-${timeRange}`)}
                    className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {clientLabel.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-slate-900">
                              {clientLabel}
                            </p>
                            <p className="text-slate-600 font-medium">
                              {timeRange}
                            </p>
                          </div>
                        </div>
                      </div>
                      {statusLabel && (
                        <Badge
                          variant="secondary"
                          className={`self-start capitalize px-3 py-1 ${
                            statusLabel.toLowerCase() === "confirmed"
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : statusLabel.toLowerCase() === "pending"
                              ? "bg-amber-100 text-amber-700 border-amber-200"
                              : statusLabel.toLowerCase() === "completed"
                              ? "bg-blue-100 text-blue-700 border-blue-200"
                              : "bg-red-100 text-red-700 border-red-200"
                          }`}>
                          {statusLabel}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                          Service Type
                        </p>
                        <p className="mt-1 text-slate-700 font-medium">
                          {massageDisplay}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                          Duration
                        </p>
                        <p className="mt-1 text-slate-700 font-medium">
                          {durationDisplay}
                        </p>
                      </div>
                    </div>
                    {notes && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs uppercase tracking-wide text-amber-600 font-medium mb-1">
                          Notes
                        </p>
                        <p className="text-sm text-amber-800">{notes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state for selected day */}
      {selectedDate && selectedDayAppointments.length === 0 && (
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-white border-b border-slate-200">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-slate-900">
                {formatDateLabel(selectedDate)}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDate(null)}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex h-48 flex-col items-center justify-center gap-4 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                <CalendarDays className="h-8 w-8 text-slate-400" />
              </div>
              <div>
                <p className="text-slate-600 font-medium">
                  No appointments scheduled
                </p>
                <p className="text-slate-500 text-sm">for this day</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CalendarView;
